import { useEffect, useRef, useState } from "react";
import { authFetch } from "../../../api";
import { cityApi, countryApi, stateApi } from "../../api/coursesApi";

const EditAddressModal = ({ open, onClose, onSave, address }) => {
  const dialogRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    fullName: "",
    country_id: "",
    state_id: "",
    city_id: "",
    zip: "",
    address: "",
    isDefault: true,
    id: null,
  });

  //  Validation rules
  const validateForm = () => {
    const newErrors = {};

    if (!form.fullName.trim()) newErrors.fullName = "Full name is required.";
    else if (form.fullName.length < 3)
      newErrors.fullName = "Full name must be at least 3 characters.";

    if (!form.country_id) newErrors.country_id = "Please select a country.";
    if (!form.state_id) newErrors.state_id = "Please select a state.";
    if (!form.city_id) newErrors.city_id = "Please select a city.";

    if (!form.zip.trim()) newErrors.zip = "Zip / Postal code is required.";
    else if (!/^\d{5,6}$/.test(form.zip))
      newErrors.zip = "Zip code must be 5–6 digits.";

    if (!form.address.trim()) newErrors.address = "Street address is required.";
    else if (form.address.length < 5)
      newErrors.address = "Street address is too short.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  }

  useEffect(() => {
    if (address) {
      setForm({
        fullName: address.name || "",
        country_id: address.country?.id || "",
        state_id: address.state?.id || "",
        city_id: address.city?.id || "",
        zip: address.zip_code || "",
        address: address.address || "",
        isDefault: address.is_default || false,
        id: address.id || null,
      });
      setIsPrefilled(true);
    }
  }, [address]);

  useEffect(() => {
    if (isPrefilled) setIsPrefilled(false);
  }, [isPrefilled]);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (form.country_id) {
      fetchStates(form.country_id);
    }
  }, [form.country_id]);

  useEffect(() => {
    if (address?.city?.id && cities.length > 0) {
      setForm((f) => ({
        ...f,
        city_id: f.city_id || address.city.id,
      }));
    }
  }, [cities, address]);

  useEffect(() => {
    if (form.state_id && form.country_id) {
      fetchCities(form.state_id, form.country_id);
    } else if (!isPrefilled) {
      setCities([]);
      setForm((f) => ({ ...f }));
    }
  }, [form.state_id, form.country_id]);

  if (!open) return null;

  const fetchCountries = async () => {
    try {
      const response = await countryApi();
      const countriesData = response.data.data;
      setCountries(countriesData);

      if (!isPrefilled) {
        const india = countriesData.find((c) => c.country_name === "India");
        if (india) setForm((f) => ({ ...f, country_id: india.id }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const response = await stateApi({ country_id: countryId });
      const statesData = response.data.data;
      setStates(statesData);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchCities = async (stateId, countryId) => {
    try {
      const response = await cityApi({
        state_id: stateId,
        country_id: countryId,
      });
      setCities(response.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const userId = localStorage.getItem("userId");

    const payload = {
      id: address.id,
      user_id: Number(userId),
      country_id: Number(form.country_id),
      state_id: Number(form.state_id),
      city_id: Number(form.city_id),
      zip_code: form.zip,
      address: form.address,
      is_default: form.isDefault,
      name: form.fullName,
    };

    try {
      const response = await authFetch("user-address/edit-address", {
        method: "PUT",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();
      if (data.success) {
        onSave?.(data.data);

        onClose?.();
      } else {
        if (
          data.message ===
          "At least one address must be set as the default address."
        ) {
          setErrors((prev) => ({
            ...prev,
            isDefault:
              "You must have at least one default address. Please keep this address as default.",
          }));
        } else {
          console.error("Unexpected error:", data.message);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[1100] flex items-center justify-center"
        role="dialog"
        aria-modal="true"
      >
        <div className="absolute inset-0 bg-black/20" onClick={onClose} />

        <div
          ref={dialogRef}
          className="relative mx-4 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl ring-1 ring-purple-200"
        >
          <div className="mb-4 flex items-start justify-between">
            <h2 className="text-[24px] font-normal leading-[100%] text-[#572575] font-siromi">
              Edit Billing Address
            </h2>
            <button
              onClick={onClose}
              className="ml-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
            >
              ✕
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 font-altisunisa">
            {/* Full Name */}
            <div>
              <label className="mb-1 block text-[12px] text-gray-700">
                Full Name<span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                placeholder="Enter Your Name"
                className="w-full rounded-md border px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
              />
              {errors.fullName && (
                <p className="text-red-600 text-xs">{errors.fullName}</p>
              )}
            </div>

            {/* Country */}
            <div>
              <label className="mb-1 block text-[12px] text-gray-700">
                Country<span className="text-rose-600">*</span>
              </label>
              <select
                name="country_id"
                value={form.country_id}
                onChange={handleChange}
                className="w-full rounded-md border px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
              >
                <option value="" disabled>
                  Select Country
                </option>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.country_name}
                  </option>
                ))}
              </select>
              {errors.country_id && (
                <p className="text-red-600 text-xs">{errors.country_id}</p>
              )}
            </div>

            {/* State */}
            <div>
              <label className="mb-1 block text-[12px] text-gray-700">
                State<span className="text-rose-600">*</span>
              </label>
              <select
                name="state_id"
                value={form.state_id}
                onChange={handleChange}
                className="w-full rounded-md border px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
              >
                <option value="" disabled>
                  Select State
                </option>
                {states.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.state_name}
                  </option>
                ))}
              </select>
              {errors.state_id && (
                <p className="text-red-600 text-xs">{errors.state_id}</p>
              )}
            </div>

            {/* City */}
            <div>
              <label className="mb-1 block text-[12px] text-gray-700">
                City<span className="text-rose-600">*</span>
              </label>
              <select
                name="city_id"
                value={form.city_id}
                onChange={handleChange}
                className="w-full rounded-md border px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
              >
                <option value="" disabled>
                  Select City
                </option>
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.city_name}
                  </option>
                ))}
              </select>
              {errors.city_id && (
                <p className="text-red-600 text-xs">{errors.city_id}</p>
              )}
            </div>

            {/* Zip */}
            <div>
              <label className="mb-1 block text-[12px] text-gray-700">
                Zip Code<span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                name="zip"
                value={form.zip}
                onChange={handleChange}
                placeholder="Enter Zip Code"
                className="w-full rounded-md border px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
              />
              {errors.zip && (
                <p className="text-red-600 text-xs">{errors.zip}</p>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="mb-1 block text-[12px] text-gray-700">
                Street Address<span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Enter Your Address"
                className="w-full rounded-md border px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
              />
              {errors.address && (
                <p className="text-red-600 text-xs">{errors.address}</p>
              )}
            </div>

            {/* Default + Buttons */}
            <div className="flex items-center justify-between">
              <div>
                <label className="mt-2 flex items-center gap-3 text-base text-[#572575]">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={form.isDefault}
                    onChange={handleChange}
                    className="h-5 w-5 border-gray-300 accent-[#572575]"
                  />
                  Set as default address
                </label>
                {errors.isDefault && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.isDefault}
                  </p>
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md bg-gray-200 px-5 py-2.5 text-[14px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-[#9D0385] px-5 py-2.5 text-white text-[14px]"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditAddressModal;
